import type { ApiClient, Method } from './client';
import { isAbortError } from './errors';

export type SseHandler = (event: string, data: unknown) => void;

/**
 * Incremental `text/event-stream` parser: feed decoded text chunks in any split; every complete event (terminated by
 * a blank line) goes to `onEvent(name, data)`. `data` is JSON-parsed when possible, else the raw string. Comment lines
 * (`: ping`) and `id:`/`retry:` fields are ignored; an event without `event:` is `message`.
 */
export function createSseParser(onEvent: SseHandler) {
  let buffer = '';
  let event = '';
  let data: string[] = [];

  const dispatch = () => {
    if (data.length) {
      const raw = data.join('\n');
      let parsed: unknown = raw;
      try {
        parsed = JSON.parse(raw);
      } catch {
        /* not JSON: keep the text */
      }
      onEvent(event || 'message', parsed);
    }
    event = '';
    data = [];
  };

  const line = (l: string) => {
    if (l === '') return dispatch();
    if (l.startsWith(':')) return;
    const i = l.indexOf(':');
    const field = i < 0 ? l : l.slice(0, i);
    let value = i < 0 ? '' : l.slice(i + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      let nl: number;
      while ((nl = buffer.search(/\r\n|\r|\n/)) >= 0) {
        // A lone trailing \r may be the first half of \r\n: wait for the next chunk.
        if (buffer[nl] === '\r' && nl === buffer.length - 1) break;
        const len = buffer[nl] === '\r' && buffer[nl + 1] === '\n' ? 2 : 1;
        line(buffer.slice(0, nl));
        buffer = buffer.slice(nl + len);
      }
    },
    /** End of stream: a final event without its blank line is still delivered. */
    flush() {
      if (buffer) {
        line(buffer.replace(/\r$/, ''));
        buffer = '';
      }
      dispatch();
    },
  };
}

/**
 * Open an SSE stream with `fetch` + bearer (no `EventSource`, so the token never goes in a URL) and call `onEvent` for
 * every event until the server ends the stream. A 401 on connect refreshes the token and reconnects once (the client's
 * `requestRaw`); non-2xx answers reject with `ApiError`. Aborting `signal` resolves quietly.
 *
 * Stable API, shared by the logs tail and the DB job streams.
 */
export async function streamSse(
  client: Pick<ApiClient, 'requestRaw'>,
  method: Method,
  path: string,
  body: unknown,
  onEvent: SseHandler,
  signal?: AbortSignal
): Promise<void> {
  const parser = createSseParser(onEvent);
  try {
    const response = await client.requestRaw(
      method,
      path,
      body === undefined ? undefined : JSON.stringify(body),
      { accept: 'text/event-stream', signal }
    );
    if (!response.body) {
      parser.push(await response.text());
      parser.flush();
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.flush();
  } catch (e) {
    if (signal?.aborted || isAbortError(e)) return;
    throw e;
  }
}
